import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  loadThemeConfig,
  loadThemeConfigFromDB,
  saveThemeConfig,
  getMergedTheme,
  ThemeConfig,
  THEME_CHANGE_EVENT,
  defaultThemeConfig,
} from '@/lib/themeConfig';

interface ThemeContextValue {
  theme: ThemeConfig;
  updateTheme: (partial: Partial<ThemeConfig>) => void;
  loading: boolean;
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: getMergedTheme(),
  updateTheme: () => {},
  loading: true,
});

function deepMerge(target: unknown, source: unknown): unknown {
  if (typeof target !== 'object' || target === null) return source;
  if (typeof source !== 'object' || source === null) return source;
  if (Array.isArray(source)) return source;
  const result = { ...(target as Record<string, unknown>) };
  for (const key of Object.keys(source as Record<string, unknown>)) {
    const sv = (source as Record<string, unknown>)[key];
    const tv = (target as Record<string, unknown>)[key];
    result[key] = deepMerge(tv, sv);
  }
  return result;
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  // Önce localStorage'dan anlık yükle (flash yok)
  const [theme, setTheme] = useState<ThemeConfig>(() => getMergedTheme());
  const [loading, setLoading] = useState(true);

  // Sayfa açılınca Supabase'den yükle ve localStorage'ı güncelle
  useEffect(() => {
    loadThemeConfigFromDB().then((dbConfig) => {
      if (dbConfig) {
        const merged = deepMerge(defaultThemeConfig, dbConfig) as ThemeConfig;
        setTheme(merged);
        // localStorage'ı da güncelle (sonraki yüklemeler için)
        try {
          localStorage.setItem('zesha_theme_v1', JSON.stringify(merged));
        } catch { /* ignore */ }
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  // Aynı sekme içi değişiklikleri dinle
  const handleThemeChange = useCallback((e: Event) => {
    const detail = (e as CustomEvent<ThemeConfig>).detail;
    if (detail) setTheme(detail);
  }, []);

  // Farklı sekme değişikliklerini dinle
  const handleStorageChange = useCallback((e: StorageEvent) => {
    if (e.key === 'zesha_theme_v1' && e.newValue) {
      try {
        setTheme(getMergedTheme());
      } catch { /* ignore */ }
    }
  }, []);

  useEffect(() => {
    window.addEventListener(THEME_CHANGE_EVENT, handleThemeChange);
    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener(THEME_CHANGE_EVENT, handleThemeChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [handleThemeChange, handleStorageChange]);

  const updateTheme = useCallback((partial: Partial<ThemeConfig>) => {
    const next = { ...theme, ...partial };
    setTheme(next);
    saveThemeConfig(next); // hem localStorage hem Supabase'e yazar
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, updateTheme, loading }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useThemeContext() {
  return useContext(ThemeContext);
}
